import { type Asset } from 'types/asset.types';
import {
  getLabelWrtUnitSystem,
  convertBasedOnUnitSystem,
  convertKmsToMiles,
} from 'utils/helpers/unitSystem';

// #rgb / #rrggbb → {r,g,b}. Returns null for any other notation so callers can
// fall back to a neutral default.
const parseHexColor = (
  hex: string
): { r: number; g: number; b: number } | null => {
  const value = hex.trim().replace('#', '');
  if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(value)) return null;
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const withAlpha = (hex: string, alpha: number): string => {
  const rgb = parseHexColor(hex);
  if (!rgb) return `rgba(255, 255, 255, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

// Rounded-rect path helper — used for the bubble body.
const roundedRectPath = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius,
    y + height
  );
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
};

// Perceived brightness (ITU-R BT.601) — decides whether text sitting on a
// status colour should be near-black or white.
const isLightColor = (hex: string): boolean => {
  const rgb = parseHexColor(hex);
  if (!rgb) return false;
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 > 150;
};

const NEUTRAL_STATUS_COLOR = '#64748b';
const ONLINE_STATUS_COLOR = '#22c55e';
// Light grey bubble with dark text — the body sits below the coloured status
// header, which keeps its own contrast handling.
const BUBBLE_COLOR = 'rgba(237, 240, 245, 0.96)';
const BODY_TEXT_COLOR = '#0f172a';
const BODY_MUTED_COLOR = 'rgba(51, 65, 85, 0.9)';
const BODY_SUBTLE_COLOR = 'rgba(71, 85, 105, 0.8)';

/** Live status of a device, as published on the SSE stream's `lastStatus`. */
export interface AssetStatusInfo {
  /** `lastStatus.colorCode` — the colour configured in Settings → Status Codes. */
  color?: string | null;
  /** `lastStatus.description` (falling back to `status`), e.g. "Ready". */
  label?: string | null;
}

export const createBillboardStyle = (
  bumperNumber: string,
  speed: number,
  _device: Asset,
  // Ignition is no longer shown as its own dot — the tooltip's status dot
  // carries the status code instead. isIgnitionOn is still used as the
  // Online/Offline fallback text for non-car customers (see below).
  isIgnitionOn: boolean,
  _isIgnitionOnRaw: boolean,
  unitSystem: string,
  t: (key: string) => string,
  extraLines: string[] = [],
  // Live status off the SSE stream. Callers only pass this for the car
  // customer — every other customer passes undefined so the header always
  // falls through to the ignition-based Online/Offline label below. When car
  // has no status yet, the fallback is a neutral slate "no status" treatment
  // (deploy status is not a connectivity signal).
  status?: AssetStatusInfo,
  useNoStatusFallback: boolean = false
) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return '';

  // Speed line is always shown. -1 means "unknown" — clamp it to 0 so the
  // tooltip never renders a negative value.
  const displaySpeed = speed === -1 ? 0 : speed;

  const statusColor = status?.color?.trim() ?? '';
  const fallbackHeaderColor =
    !useNoStatusFallback && isIgnitionOn
      ? ONLINE_STATUS_COLOR
      : NEUTRAL_STATUS_COLOR;
  const headerColor = statusColor || fallbackHeaderColor;
  const fallbackHeaderText = useNoStatusFallback
    ? t('asset_map_status_no_status')
    : isIgnitionOn
    ? 'Online'
    : 'Offline';
  const headerText = (
    status?.label?.trim() ?? fallbackHeaderText
  ).toUpperCase();
  // Status codes are authored for light UI, so the header text flips to
  // near-black on pale codes (e.g. the yellow "Waiting" ones).
  const headerTextColor = isLightColor(headerColor) ? '#0f172a' : '#ffffff';

  const speedText = `${t('speed')}: ${convertBasedOnUnitSystem(
    convertKmsToMiles,
    displaySpeed,
    unitSystem
  ).toFixed(2)} ${getLabelWrtUnitSystem('KMH', unitSystem).toLowerCase()}`;

  // Layout in CSS pixels, then render at 2x so the label stays crisp when
  // Cesium scales the billboard down (see the `scale` on the Entity).
  const scaleFactor = 2;
  const paddingX = 11;
  const headerFont = 'bold 16px sans-serif';
  const titleFont = 'bold 22px sans-serif';
  const bodyFont = '600 18px sans-serif';
  const extraFont = '600 17px sans-serif';
  const headerHeight = 26;
  const dotRadius = 4.5;
  const headerGap = 7;
  const lineHeight = 21;
  const bodyPaddingY = 8;

  // Width is measured off the real strings so long bumper numbers and the
  // "Last seen" line are never clipped, and short ones don't get a huge bubble.
  const measure = (text: string, font: string) => {
    context.font = font;
    return context.measureText(text).width;
  };
  const contentWidth = Math.max(
    measure(headerText, headerFont) + dotRadius * 2 + headerGap,
    measure(bumperNumber, titleFont) + dotRadius * 2 + headerGap,
    measure(speedText, bodyFont),
    ...extraLines.map((line) => measure(line, extraFont))
  );
  const width = Math.ceil(paddingX * 2 + contentWidth);
  // Header + (bumper line ≈ 22px) + speed line + one line per extra, padded.
  const height = Math.ceil(
    headerHeight +
      bodyPaddingY * 2 +
      22 +
      lineHeight * (1 + extraLines.length) -
      2
  );
  const pointerHeight = 7;
  // Breathing room so the drop shadow fades out instead of being cut off flat
  // at the canvas edge.
  const margin = 4;

  canvas.width = (width + margin * 2) * scaleFactor;
  canvas.height = (height + pointerHeight + margin * 2) * scaleFactor;
  context.scale(scaleFactor, scaleFactor);
  context.translate(margin, margin);
  context.textBaseline = 'middle';

  // Bubble body — near-opaque light grey so the label always wins against the
  // terrain and the coloured site polygons underneath.
  const radius = 8;
  context.save();
  context.shadowColor = 'rgba(15, 23, 42, 0.35)';
  context.shadowBlur = 6;
  context.shadowOffsetY = 2;
  roundedRectPath(context, 0.75, 0.75, width - 1.5, height - 1.5, radius);
  context.fillStyle = BUBBLE_COLOR;
  context.fill();
  context.restore();

  // Coloured status header, clipped to the bubble so it keeps the top corners.
  context.save();
  roundedRectPath(context, 0.75, 0.75, width - 1.5, height - 1.5, radius);
  context.clip();
  context.fillStyle = headerColor;
  context.fillRect(0, 0, width, headerHeight);
  context.restore();

  context.strokeStyle = withAlpha(headerColor, 0.9);
  context.lineWidth = 1.5;
  roundedRectPath(context, 0.75, 0.75, width - 1.5, height - 1.5, radius);
  context.stroke();

  // Pointer/connector down to the truck icon.
  context.beginPath();
  context.moveTo(width / 2 - 6, height - 1);
  context.lineTo(width / 2 + 6, height - 1);
  context.lineTo(width / 2, height + pointerHeight - 1);
  context.closePath();
  context.fillStyle = BUBBLE_COLOR;
  context.fill();

  const textLeft = paddingX;
  context.textAlign = 'left';

  // Header: status-code dot followed by the status name. The dot carries the
  // same colour as the header band it sits on, so it needs a contrasting ring
  // to stay visible — it reads as a bullet for the status, matching the dot
  // pinned to the truck icon on the map.
  const headerY = headerHeight / 2;
  context.beginPath();
  context.arc(textLeft + dotRadius, headerY, dotRadius, 0, Math.PI * 2);
  context.fillStyle = headerColor;
  context.fill();
  context.lineWidth = 1.5;
  context.strokeStyle = headerTextColor;
  context.stroke();

  context.font = headerFont;
  context.fillStyle = headerTextColor;
  context.fillText(headerText, textLeft + dotRadius * 2 + headerGap, headerY);

  // Body: bumper number, speed, then any extra lines (e.g. "Last seen").
  const titleY = headerHeight + bodyPaddingY + 11;
  context.font = titleFont;
  context.fillStyle = BODY_TEXT_COLOR;
  context.fillText(bumperNumber, textLeft, titleY);

  context.font = bodyFont;
  context.fillStyle = BODY_MUTED_COLOR;
  context.fillText(speedText, textLeft, titleY + lineHeight);

  context.font = extraFont;
  context.fillStyle = BODY_SUBTLE_COLOR;
  extraLines.forEach((line, index) => {
    context.fillText(line, textLeft, titleY + lineHeight * (index + 2));
  });

  return canvas.toDataURL();
};

const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

const isWithinRangeOfPits = (
  lat: number,
  lon: number,
  pitsCoords: Array<{ lat: number; lon: number }>,
  maxDistance: number = 50
): boolean => {
  if (!pitsCoords.length) return true; // If no pits, consider it valid
  return pitsCoords.some(
    (pit) => calculateDistance(lat, lon, pit.lat, pit.lon) <= maxDistance
  );
};

export const isValidCoordinate = (
  longitude: number | null | undefined,
  latitude: number | null | undefined,
  pitsCoords: Array<{ lat: number; lon: number }>,
  engineHoursScreen: boolean = false
): boolean => {
  if (
    longitude === null ||
    latitude === null ||
    longitude === undefined ||
    latitude === undefined ||
    typeof longitude !== 'number' ||
    typeof latitude !== 'number' ||
    isNaN(longitude) ||
    isNaN(latitude) ||
    longitude < -180 ||
    longitude > 180 ||
    latitude < -90 ||
    latitude > 90 ||
    Number(longitude) === -1 ||
    Number(latitude) === -1 ||
    longitude === -1 ||
    latitude === -1
  ) {
    return false;
  }

  if (engineHoursScreen) {
    return true;
  } else {
    return isWithinRangeOfPits(Number(latitude), Number(longitude), pitsCoords);
  }
};

// `screenPoint` is the click's on-screen position (viewport-relative CSS
// pixels) — the caller derives it however is appropriate for its map (e.g.
// MapLibre's `map.project([lon, lat])` plus the map container's bounding
// rect). Previously this computed that point itself via Cesium's
// `scene.cartesianToCanvasCoordinates`, which required a live Cesium viewer
// ref; decoupling it from any particular map library lets this stay shared
// business logic (info-box placement) rather than a rendering adapter.
export const handleEntityClick = (
  asset: Asset | undefined,
  screenPoint: { x: number; y: number } | null | undefined,
  setselectedAsset: (asset: Asset | null) => void,
  setShowTail: (show: boolean) => void,
  setClickPosition: (position: {
    x: number;
    y: number;
    displayAbove: boolean;
  }) => void,
  sidebar?: boolean
) => {
  if (!asset) {
    setselectedAsset(null);
    return;
  }

  const point = screenPoint ?? { x: 400, y: 100 };

  setselectedAsset(asset);
  sidebar ? setShowTail(false) : setShowTail(true);

  // Calculate the available space below the clicked asset
  const screenHeight = window.innerHeight;
  const spaceBelow = screenHeight - point.y;

  // Set a threshold for the minimum space required below the asset
  const minSpaceRequired = 300;

  // Determine if the info box should be displayed above or below the asset
  const displayAbove = spaceBelow < minSpaceRequired;

  // Update the click position and whether to show the tail
  setClickPosition({
    x: point.x,
    y: point.y,
    displayAbove,
  });
};

export const LIVE_POSITION_MAX_AGE_MS = 2 * 60 * 1000; // 2 minutes

// Informational only — whether a fix is within the last 2 minutes. Never use
// this to decide whether to show a truck; it only flags a fix as stale so the
// UI can indicate that, while the truck stays visible at its last known spot.
export const isLivePosition = (latestTs: unknown): boolean => {
  const ts = Number(latestTs);
  if (!ts || isNaN(ts)) return false;
  return Date.now() - ts <= LIVE_POSITION_MAX_AGE_MS;
};

export const hasAssetMoved = (
  lastResponse: any,
  newResponse: any,
  deviceId: string
) => {
  if (!lastResponse || !newResponse) return false;

  const lastPos = lastResponse[deviceId];
  const newPos = newResponse[deviceId];

  if (!lastPos || !newPos) return false;

  const lastLon = Number(lastPos.gps_longitude);
  const lastLat = Number(lastPos.gps_latitude);
  const newLon = Number(newPos.gps_longitude);
  const newLat = Number(newPos.gps_latitude);

  return (
    !isNaN(lastLon) &&
    !isNaN(lastLat) &&
    !isNaN(newLon) &&
    !isNaN(newLat) &&
    (Math.abs(lastLon - newLon) > 0.000001 ||
      Math.abs(lastLat - newLat) > 0.000001)
  );
};

/**
 * @param data
 * @param hasAssetMoved
 * @returns The calculated speed value:
 *   - 0: Asset is off/ignition disabled, data is stale (>2 hours old), or asset hasn't moved but speed sensors report non-zero
 *   - -1: Unknown speed (asset has moved but speed is 0, OR asset hasn't moved and speed is 0)
 *   - >0: Actual speed value
 *
 * Logic Flow:
 * 1. Validates ignition status and data freshness (must be within 2 hours)
 * 2. Extracts speed readings from j1939_wheel_based_vehicle_speed and cdl_ground_speed sensors
 * 3. Calculates speed using both sensors if available, otherwise prioritizes j1939 over cdl
 * 4. Applies movement-based validation:
 *    - If asset moved but speed is 0: returns -1 (unknown)
 *    - If asset hasn't moved and speed is 0: returns 0.
 *    - If asset hasn't moved but speed > 0: returns 0 (sensor error)
 *    - Otherwise: returns calculated speed
 */
export const assetMovementSpeed = (data: any): number => {
  const TWO_HOURS_AGO = Date.now() - 2 * 60 * 60 * 1000;

  if (!data?.sb_ignition_status || (data?.latest_ts ?? 0) < TWO_HOURS_AGO) {
    return 0;
  }

  const j1939Speed: number | null =
    data?.j1939_wheel_based_vehicle_speed_ts &&
    data.j1939_wheel_based_vehicle_speed_ts > TWO_HOURS_AGO &&
    (data.j1939_wheel_based_vehicle_speed ?? 0) !== 0
      ? data.j1939_wheel_based_vehicle_speed ?? 0
      : null;

  const cdlSpeed: number | null =
    data?.cdl_ground_speed_ts &&
    data.cdl_ground_speed_ts > TWO_HOURS_AGO &&
    (data.cdl_ground_speed ?? 0) !== 0
      ? data.cdl_ground_speed ?? 0
      : null;

  const gpsVelocity: number | null =
    data?.gps_velocity_ts &&
    data.gps_velocity_ts > TWO_HOURS_AGO &&
    (data.gps_velocity ?? 0) !== 0
      ? data.gps_velocity ?? 0
      : null;

  // Filter out the null sensors and take the last available value.
  const availableSpeeds = [j1939Speed, cdlSpeed, gpsVelocity].filter(
    (s): s is number => s !== null
  );
  const calculatedSpeed = availableSpeeds.length
    ? availableSpeeds[availableSpeeds.length - 1]
    : 0;

  // Show the last available value. Ignition is on here (checked above), so a
  // 0 means the truck is on but not moving — show it as 0 rather than hiding it.
  return calculatedSpeed;
};
